import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useAnimateOnScroll } from '../hooks/useAnimateOnScroll';
import { FaTractor, FaUsers, FaClipboardList, FaCheckCircle, FaMapMarkerAlt, FaStar, FaClock } from 'react-icons/fa';
import './LiveMetrics.css';

const METRICS_CONFIG = [
  { key: 'farmsOnboarded', label: 'Farmers', icon: FaTractor },
  { key: 'serviceProvidersRegistered', label: 'Providers', icon: FaUsers },
  { key: 'serviceRequestsSubmitted', label: 'Service Requests', icon: FaClipboardList },
  { key: 'completedServices', label: 'Completed Services', icon: FaCheckCircle },
  { key: 'activeRegions', label: 'Active Regions', icon: FaMapMarkerAlt },
  { key: 'averageServiceRating', label: 'Avg. Service Rating', icon: FaStar, suffix: '/5', decimals: 1 },
  { key: 'onTimeCompletionRatePercent', label: 'On-Time Completion', icon: FaClock, suffix: '%' },
];

function AnimatedNumber({ value, decimals = 0, suffix = '', isInView }) {
  const target = value ?? 0;
  const [display, setDisplay] = useState(target);

  useEffect(() => {
    if (!isInView) {
      setDisplay(target);
      return;
    }
    const duration = 1500;
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(target * eased);
      if (progress < 1) requestAnimationFrame(tick);
      else setDisplay(target);
    };

    requestAnimationFrame(tick);
  }, [isInView, target]);

  const formatted =
    decimals > 0
      ? display.toFixed(decimals)
      : Math.round(display).toLocaleString();
  return (
    <span className="live-metrics-value">
      {formatted}
      {suffix}
    </span>
  );
}

function LiveMetrics() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRetry, setShowRetry] = useState(false);

  const [ref, isInView] = useAnimateOnScroll({ threshold: 0.15 });

  const fetchMetrics = React.useCallback(async () => {
    setShowRetry(false);
    const { data, error: err } = await api.getPublicMetrics();
    if (!err && data) {
      setMetrics({
        farmsOnboarded: data.farmsOnboarded ?? 0,
        serviceProvidersRegistered: data.serviceProvidersRegistered ?? 0,
        serviceRequestsSubmitted: data.serviceRequestsSubmitted ?? 0,
        completedServices: data.completedServices ?? 0,
        activeRegions: data.activeRegions ?? 0,
        averageServiceRating: data.averageServiceRating ?? null,
        onTimeCompletionRatePercent: data.onTimeCompletionRatePercent ?? null,
      });
      setLoading(false);
      return;
    }
    setShowRetry(true);
    setMetrics({});
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 60000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  if (loading && !metrics) {
    return (
      <section className="section section-live-metrics">
        <div className="section-inner">
          <h2 className="section-title">Live Platform Metrics</h2>
          <div className="live-metrics-grid">
            {METRICS_CONFIG.map((_, i) => (
              <div key={i} className="live-metrics-card live-metrics-loading">
                <span className="live-metrics-value">—</span>
                <span className="live-metrics-label">Loading…</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="live-metrics" className="section section-live-metrics" ref={ref}>
      <div className="section-inner">
        <h2 className="section-title">Live Platform Metrics</h2>
        <p className="section-subtitle">
          Real-time coordination data from the Digilync platform.
        </p>
        {showRetry && (
          <div className="live-metrics-error">
            <button type="button" className="live-metrics-retry" onClick={fetchMetrics}>Retry</button>
          </div>
        )}
        <div className="live-metrics-grid">
          {METRICS_CONFIG.map(({ key, label, icon: Icon, suffix = '', decimals = 0 }) => {
            const val = metrics?.[key];
            const isNull = val == null && (key === 'averageServiceRating' || key === 'onTimeCompletionRatePercent');
            return (
              <div key={key} className="live-metrics-card">
                <Icon className="live-metrics-icon" aria-hidden />
                {isNull ? (
                  <span className="live-metrics-value">—</span>
                ) : (
                  <AnimatedNumber
                    value={val ?? 0}
                    decimals={decimals}
                    suffix={suffix}
                    isInView={isInView}
                  />
                )}
                <span className="live-metrics-label">{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default LiveMetrics;
